import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save, Check, X, KeyRound, Bell } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SectionTitle, Panel } from "@/components/platform/PortalLayout";

const inputCls = "w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";
const labelCls = "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5";
const NOTIF_OPTIONS = [
  { key: "workout_assigned", label: "Workout assigned or updated" },
  { key: "coach_feedback", label: "Coach feedback added" },
  { key: "evaluation", label: "Evaluations & results" },
  { key: "achievements", label: "Badges & Speed Points" },
  { key: "schedule", label: "Schedule changes" },
  { key: "membership", label: "Membership updates" },
];

export default function AccountSettings({ athlete, account, onSaved }) {
  const [username, setUsername] = useState(account?.username || "");
  const [email, setEmail] = useState(account?.email || "");
  const [phone, setPhone] = useState(athlete?.phone || "");
  const [photo, setPhoto] = useState(athlete?.photo_url || "");
  const [avail, setAvail] = useState(null);
  const [savingAcct, setSavingAcct] = useState(false);
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [savingPw, setSavingPw] = useState(false);
  const [prefs, setPrefs] = useState({});
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => { api.get("/auth/notification-prefs").then(({ data }) => setPrefs(data.prefs || {})).catch(() => {}); }, []);

  const checkUser = useCallback(async (u) => {
    if (!u || u === account?.username) { setAvail(null); return; }
    try { const { data } = await api.get(`/auth/username-available?username=${encodeURIComponent(u)}`); setAvail(data.available); }
    catch { setAvail(null); }
  }, [account]);
  useEffect(() => { const t = setTimeout(() => checkUser(username.trim()), 400); return () => clearTimeout(t); }, [username, checkUser]);

  const saveAccount = async () => {
    setSavingAcct(true);
    try {
      const acctBody = {};
      if (username.trim() && username.trim() !== account?.username) acctBody.username = username.trim();
      if (email.trim() && email.trim() !== account?.email) acctBody.email = email.trim();
      if (Object.keys(acctBody).length) await api.patch("/auth/account", acctBody);
      if (phone !== (athlete?.phone || "") || photo !== (athlete?.photo_url || ""))
        await api.patch(`/athletes/${athlete.id}`, { phone, photo_url: photo });
      toast.success("Account updated.");
      onSaved?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSavingAcct(false); }
  };

  const savePassword = async () => {
    if (nw.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    setSavingPw(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password changed."); setCur(""); setNw(""); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSavingPw(false); }
  };

  const savePrefs = async () => {
    setSavingPrefs(true);
    try { await api.patch("/auth/notification-prefs", { prefs }); toast.success("Notification preferences saved."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSavingPrefs(false); }
  };

  return (
    <div data-testid="account-settings" className="space-y-8">
      <div>
        <SectionTitle>Account Settings</SectionTitle>
        <Panel>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Username</label>
              <div className="relative">
                <input data-testid="settings-username" value={username} onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))} className={`${inputCls} pr-9`} />
                {avail === true && <Check size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-400" />}
                {avail === false && <X size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400" />}
              </div>
              {avail === false && <p className="text-red-400 text-xs mt-1" data-testid="username-taken">Username is taken</p>}
            </div>
            <div><label className={labelCls}>Email</label><input data-testid="settings-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Phone Number</label><input data-testid="settings-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Profile Photo URL</label><input data-testid="settings-photo" value={photo} onChange={(e) => setPhoto(e.target.value)} className={inputCls} /></div>
          </div>
          <button data-testid="settings-save-account" onClick={saveAccount} disabled={savingAcct || avail === false} className="mt-5 inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">{savingAcct ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        </Panel>
      </div>

      <div>
        <SectionTitle>Change Password</SectionTitle>
        <Panel>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Current Password</label><input data-testid="settings-cur-pw" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>New Password</label><input data-testid="settings-new-pw" type="password" value={nw} onChange={(e) => setNw(e.target.value)} className={inputCls} /></div>
          </div>
          <button data-testid="settings-save-pw" onClick={savePassword} disabled={savingPw || !cur || !nw} className="mt-5 inline-flex items-center gap-2 border border-white/25 font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-60">{savingPw ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Update Password</button>
        </Panel>
      </div>

      <div>
        <SectionTitle>Notifications</SectionTitle>
        <Panel>
          <div className="space-y-3">
            {NOTIF_OPTIONS.map((o) => (
              <label key={o.key} data-testid={`notif-${o.key}`} className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={prefs[o.key] !== false} onChange={(e) => setPrefs((p) => ({ ...p, [o.key]: e.target.checked }))} className="w-4 h-4 accent-white" />
                <span className="text-sm text-white/75">{o.label}</span>
              </label>
            ))}
          </div>
          <button data-testid="settings-save-prefs" onClick={savePrefs} disabled={savingPrefs} className="mt-5 inline-flex items-center gap-2 border border-white/25 font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-60">{savingPrefs ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />} Save Preferences</button>
        </Panel>
      </div>
    </div>
  );
}
